<template>
  <!-- Backdrop dismissal is off on purpose (the old overlay had none: a stray
       click must not drop a half-staged setup); Escape and the ✕ close it,
       and neither exists mid-commit. -->
  <Modal
    :open="open"
    size="lg"
    :title="t('onboarding.wizard.title')"
    :subtitle="isCommitting ? t('onboarding.wizard.subtitleCommitting') : t('onboarding.wizard.subtitle')"
    :close-label="t('common.close')"
    :dismissible="!isCommitting"
    :dismiss-on-backdrop="false"
    data-test="suite-wizard"
    @close="close"
  >
    <!-- Commit in flight → the replay progress screen (unless recovering from a
         replay error, when we show the editable sections again). -->
    <WizardCommitScreen
      v-if="isCommitting && !recovering"
      data-test="wizard-commit"
      @back-to-setup="onBackToSetup"
    />

    <div v-else>
      <!-- Recovery: the last Finish failed; the admin fixes the input and finishes
           again. It names the step that failed and the reason the seam gave — no
           field is ever marked, so "fix the highlighted field" pointed at nothing
           (issue 795). -->
      <div v-if="recovering" data-test="wizard-recovery" class="mb-4 space-y-1 rounded-lg border border-warning-soft bg-warning-soft px-4 py-3 text-sm text-warning-text">
        <p>{{ t('onboarding.wizard.recovery', { step: stepLabel(recoveryStepKey) }) }}</p>
        <p v-if="recoveryReason" data-test="wizard-recovery-reason">{{ recoveryReason }}</p>
      </div>

      <!-- Stepper: clickable bubbles (jump to any step), no status. -->
      <ol class="mb-6 flex flex-wrap gap-2">
        <li v-for="(step, i) in steps" :key="step.key">
          <button
            type="button"
            :data-test="`wizard-step-${step.key}`"
            class="rounded-full px-3 py-1 text-xs font-medium transition-colors"
            :class="i === currentIndex ? 'bg-accent-soft text-accent-text' : 'bg-surface-wash text-secondary hover:bg-[var(--pill-ring)]'"
            :disabled="busy"
            @click="goTo(i)"
          >
            {{ stepLabel(step.key) }}
          </button>
        </li>
      </ol>

      <!-- Active section. -->
      <div class="rounded-lg border border-default bg-surface-wash p-5 md:p-6">
        <component :is="componentFor(currentStep.key)" ref="sectionRef" :key="currentStep.key" :module-key="currentStep.key" />
      </div>

      <!-- A refused Finish, rendered where the press happened (#364 F19). The
           commit is the one action in this wizard with nothing else on screen
           to show its outcome, and being dropped somewhere with no message is
           precisely the defect. Stays until the next attempt clears it. -->
      <InlineNotice v-if="commitError" variant="error" class="mt-6" data-test="wizard-commit-error">
        {{ commitError }}
      </InlineNotice>
    </div>

    <!-- Footer: only Zurück / Weiter. On the Finish step, Weiter commits. -->
    <template v-if="!(isCommitting && !recovering)" #footer>
      <Button v-if="currentIndex > 0" variant="secondary" :disabled="busy" @click="goBack">
        {{ t('onboarding.actions.back') }}
      </Button>
      <Button data-test="wizard-next" variant="primary" :loading="busy" @click="onNext">
        {{ isFinishStep ? t('onboarding.wizard.finish.cta') : t('onboarding.actions.next') }}
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { Button, Modal, InlineNotice } from '@lumi/ui';
import { useApiMutation } from '@lumi/app-kit';
import { useOwnDetailsSkipWarning } from '../composables/useOwnDetailsSkipWarning';
import { userApi } from '../utils/api';
import { useAuthStore } from '../stores/auth';
import { useOnboardingStore, type OnboardingStatus } from '../stores/onboarding';
import { useTenantsStore } from '../stores/tenants';
import WizardDetailsSection from '../components/onboarding/WizardDetailsSection.vue';
import WizardGeneralSection from '../components/onboarding/WizardGeneralSection.vue';
import WizardModuleSection from '../components/onboarding/WizardModuleSection.vue';
import WizardFinishSection from '../components/onboarding/WizardFinishSection.vue';
import WizardCommitScreen from '../components/onboarding/WizardCommitScreen.vue';

// The suite setup wizard, now a MODAL over the workspace (issue 214): opened by the fork
// / demo banner "set up" CTA (which route to /?setup=1), closable with the ✕ back to
// the (still-live) demo. It also opens itself when a commit is in flight so a
// hard-refresh mid-commit resumes on the progress screen. Every step is optional and
// nothing is written until Finish (ADR 0032). first_people was removed — invites are
// done in the admin area after setup.
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const onboardingStore = useOnboardingStore();
const tenantsStore = useTenantsStore();

const MODULE_ORDER = ['ats', 'time_tracking', 'apprenticeship', 'people'] as const;

type Step = { key: string };
// "Your details" comes first, for the OWNER alone (ADR 0088 §8.3): her Person is
// confirmed there, and confirming one's own record is hers only — a suite_admin
// running the wizard has no such step.
const steps = computed<Step[]>(() => {
  const moduleSteps: Step[] = MODULE_ORDER.filter((m) => onboardingStore.checkedOutModules.includes(m)).map((key) => ({ key }));
  const details: Step[] = tenantsStore.isTenantOwner(tenantsStore.activeTenant) ? [{ key: 'details' }] : [];
  return [...details, { key: 'general' }, ...moduleSteps, { key: 'finish' }];
});

const currentIndex = ref(0);
const currentStep = computed<Step>(() => steps.value[currentIndex.value] ?? steps.value[0]);
const isFinishStep = computed(() => currentStep.value.key === 'finish');

const sectionRef = ref<{ persistComplete?: () => Promise<boolean>; stillUnconfirmed?: () => Promise<boolean> } | null>(null);
const busy = ref(false);
// A commit error sent us back to the editable sections (mode is still 'applying'),
// opened on the step that failed, with the reason it failed.
const recovering = ref(false);
const recoveryStepKey = ref('general');
const recoveryReason = ref<string | null>(null);

const isCommitting = computed(() => onboardingStore.mode === 'wiping' || onboardingStore.mode === 'applying');

// Open when the URL asks for it (?setup=1) or a commit is mid-flight, for an
// authenticated admin who has not resolved onboarding.
const open = computed(
  () =>
    authStore.isAuthenticated &&
    onboardingStore.isAdmin &&
    !onboardingStore.firstRunResolved &&
    (route.query.setup === '1' || isCommitting.value),
);

// Re-open fresh → start at the first step (unless resuming a commit).
watch(open, (isOpen, was) => {
  if (isOpen && !was && !isCommitting.value) currentIndex.value = 0;
});

function componentFor(key: string) {
  if (key === 'details') return WizardDetailsSection;
  if (key === 'general') return WizardGeneralSection;
  if (key === 'finish') return WizardFinishSection;
  return WizardModuleSection;
}
function stepLabel(key: string): string {
  if (key === 'details') return t('onboarding.wizard.details.step');
  if (key === 'general') return t('onboarding.wizard.general.title');
  if (key === 'finish') return t('onboarding.wizard.finish.step');
  return t(`onboarding.wizard.sections.${key}.title`);
}

// Stage the current section (no module write). A section can veto (invalid input,
// e.g. a malformed ATS contact e-mail) by returning false — stay put so it can be
// fixed. The Finish section has no persistComplete, so it never vetoes.
async function stageCurrent(): Promise<boolean> {
  const ok = await sectionRef.value?.persistComplete?.();
  return ok !== false;
}

async function goTo(index: number) {
  if (busy.value || index === currentIndex.value) return;
  busy.value = true;
  try {
    if (!(await stageCurrent())) return;
    currentIndex.value = index;
  } finally {
    busy.value = false;
  }
}
async function goBack() {
  if (currentIndex.value > 0) await goTo(currentIndex.value - 1);
}
// Finish = commit: wipe-if-demo, then the commit screen drives the replay.
// `inlineEdit` because success is visible (the wizard hands over to the commit
// screen), and `inPlace` because a refused Finish has to STAY on screen: this is
// the press #364 F19 is about, and a toast that fades while the user is still
// looking at a Finish button is indistinguishable from nothing having happened.
// `commitError` is rendered on the finish step, right above the button that failed.
const { formError: commitError, mutate: beginCommit } = useApiMutation<void, []>({
  tier: 'inlineEdit',
  errorChannel: 'inPlace',
  run: async () => {
    const tenantId = await onboardingStore.activeTenantId();
    if (!tenantId) return;
    const res = await userApi.post(`${onboardingStore.base(tenantId)}/commit/begin`, { empty: false });
    onboardingStore.apply(res.data as OnboardingStatus);
  },
});

// The fork owns the whole AVV-acceptance UX; `?avv=1` makes it open that step
// immediately and hand back to the wizard once it is signed. `?avv=1` is also what
// makes the fork EXPLAIN the move (#364 F19): this is the one navigation that takes
// a user off the wizard mid-press, and it must never read as "Finish did nothing".
function toAvvStep() {
  router.push({ path: '/onboarding', query: { avv: '1' } });
}

async function onNext() {
  if (busy.value) return;
  if (isFinishStep.value) {
    busy.value = true;
    try {
      if (recovering.value) {
        // Resuming after a replay error: the sections were re-staged; hand back to the
        // commit screen, which re-runs the replay (applied steps skipped).
        recovering.value = false;
      } else {
        // #332/#364 F7: commit/begin fail-closed-gates on AVV acceptance, and this
        // wizard can be entered WITHOUT passing the fork that asks for it — the demo
        // banner's CTA, a bookmarked `?setup=1`, a restored tab. Ask BEFORE the
        // write: refuse-then-toast is not a gate the admin can act on, and it is
        // what made the first Finish fail with no AVV ever presented. The status is
        // re-read first so a stale client does not send the admin to sign something
        // they already signed.
        await onboardingStore.fetchStatus(true);
        if (!onboardingStore.avvAccepted) {
          toAvvStep();
          return;
        }
        const outcome = await beginCommit();
        // Backstop for the race the pre-check cannot close (acceptance revoked, a
        // second admin, a client that skipped the check): route to the same AVV
        // step rather than leaving the admin on a wizard step with no acceptance
        // affordance (a message alone is not a way forward). Every OTHER failure
        // stays right here and renders `commitError` above the button.
        if (!outcome.ok && outcome.code === 'ONBOARDING_AVV_ACCEPTANCE_REQUIRED') {
          toAvvStep();
          return;
        }
      }
    } finally {
      busy.value = false;
    }
    return;
  }
  await goTo(currentIndex.value + 1);
}

// Commit error → show the editable sections again, on the step that failed (issue
// 795), so the admin can fix and finish again. The company name (`__company`) and
// anything unplaceable land on general, where the company name lives — never on
// "Your details": the owner's Person was confirmed before the commit and is not
// what failed.
function onBackToSetup(failedKey?: string | null, reason?: string | null) {
  const generalIndex = steps.value.findIndex((s) => s.key === 'general');
  const failedIndex = failedKey ? steps.value.findIndex((s) => s.key === failedKey) : -1;
  const index = failedIndex >= 0 && steps.value[failedIndex].key !== 'details' ? failedIndex : generalIndex;
  recovering.value = true;
  recoveryStepKey.value = steps.value[Math.max(0, index)]?.key ?? 'general';
  recoveryReason.value = reason || null;
  currentIndex.value = Math.max(0, index);
}
// The details step cannot be stepped past while the owner's record is unconfirmed;
// the one way out is closing the wizard. That is a skip like any other, so it goes
// through the same warning as the prompt on Suite Home (which then keeps asking).
const { confirmSkip } = useOwnDetailsSkipWarning();
async function close() {
  if (isCommitting.value) return;
  if (currentStep.value.key === 'details' && (await sectionRef.value?.stillUnconfirmed?.())) {
    if (!(await confirmSkip())) return;
  }
  const query = { ...route.query };
  delete query.setup;
  router.replace({ path: route.path === '/onboarding' ? '/' : route.path, query });
}
</script>
